<i18n src="./resources/locales.json"></i18n>
<template>
  <div class="ma-2 ecs-notification-item" :class="{ expanded: open }">
    <div class="surface lighten-1 rounded-corners elevation-1">
      <div class="header pa-2" @click="toggle">
        <div class="notification-type-icon">
          <v-icon :style="'color: ' + item.iconColor" small>
            {{ item.icon }}
          </v-icon>
          <span class="px-2 tiny-transparency">
            {{ $t(item.notificationType) }}
          </span>
        </div>
        <div class="notification-time">
          <span class="px-2 tiny-transparency">
            {{ item.timeAgo }}
          </span>
          <v-icon :class="{ 'open icon': open, icon: !open }">
            mdi-chevron-down
          </v-icon>
        </div>
      </div>
      <div class="body px-2" :class="{ 'content-open': open, 'content-closed': !open }">
        <h4 class="font-weight-bold my-2">
          {{ $t(item.headline.i18nKey, { date: item.headline.date || '' }) }}
        </h4>
        <p ref="message_p" :class="messageClass">
          {{ $t(item.message, item.messageVars) }}
        </p>
        <div v-if="item.customerFullName" class="cb-info">
          <h3>{{ $t('callback.additionalInfo.customerFullName') }}</h3>
          <p>{{ item.customerFullName }}</p>
        </div>
        <div v-if="remark" class="cb-info">
          <h3>{{ $t('callback.additionalInfo.lastRemark') }}</h3>
          <p>{{ remark }}</p>
        </div>
        <div v-if="item.numberOfPreviousAttempts" class="cb-info">
          <h3>{{ $t('callback.additionalInfo.numberOfPreviousAttempts') }}</h3>
          <p>{{ item.numberOfPreviousAttempts }}</p>
        </div>
        <div v-if="item.assignedTeamName" class="cb-info">
          <h3>{{ $t('callback.additionalInfo.assignedTeamName') }}</h3>
          <p>{{ item.assignedTeamName }}</p>
        </div>
        <div v-if="businessProcessName" class="cb-info">
          <h3>{{ $t('callback.additionalInfo.businessProcessName') }}</h3>
          <p>{{ businessProcessName }}</p>
        </div>
      </div>
      <audio v-if="item.callbackRecordingFile" controls :src="item.callbackRecordingFile" :dark="$vuetify.theme.dark">
        Your browser does not support the
        <code>audio</code> element
      </audio>
      <div class="actions">
        <v-btn
          v-for="(action, index) in item.actions"
          :key="'action_' + index"
          class="mb-2 mr-2 elevation-0 phone-btn"
          color="primary"
          small
          :loading="isWaitingCallbackOfferEvent"
          @click="() => onActionClick(action.action)"
        >
          <v-icon v-if="isCallback" small class="phone">mdi-phone</v-icon>
          {{ $t(action.name) }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'EcsNotificationItem',

  props: {
    isFirst: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      open: false
    };
  },

  computed: {
    ...mapGetters('BUSINESS_PROCESSES', ['getBusinessProcessById']),

    ...mapGetters('EVENT_TIMING', ['isWaitingForEvent']),

    isWaitingCallbackOfferEvent() {
      return this.isWaitingForEvent({
        interactionId: this.item.interactionId,
        constraintNames: ['INTERACTION_OFFERED_TO_AGENT_EVENT']
      });
    },

    messageClass() {
      return this.isFirst ? 'message' : 'message closed';
    },

    businessProcessName() {
      const bp = this.getBusinessProcessById(this.item.businessProcessId);
      return bp ? bp.name : null;
    },

    remark() {
      return this.item ? (this.item.lastRemark || '').replaceAll(/API Callback \(.{24,30}\): /g, '') : '<br>';
    },

    isCallback() {
      return this.item ? this.item.notificationType.includes('callback') : false;
    }
  },

  mounted() {
    if (this.isFirst) {
      this.open = true;
    }
  },

  methods: {
    toggle() {
      this.open = !this.open;
      if (this.open) {
        this.$refs.message_p.classList.remove('closed');
      } else {
        setTimeout(() => {
          this.$refs.message_p.classList.add('closed');
        }, 250);
      }
    },
    onActionClick(action) {
      if (this.isWaitingCallbackOfferEvent) {
        return;
      }
      this.$emit('onItemClick', this.item, action);
    }
  }
};
</script>

<style scoped>
.tiny-transparency {
  opacity: 0.8;
}
.ecs-notification-item {
  height: auto;
  -webkit-transition: height 1s !important;
  transition: height 1s !important;
}

.ecs-notification-item .header {
  height: 24px;
  cursor: pointer;
}

.ecs-notification-item .body {
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
}
.ecs-notification-item .body.content-closed {
  max-height: 60px;
  transition: all 250ms linear;
}
.ecs-notification-item .body.content-open {
  transition: all 250ms linear;
}
.ecs-notification-item p.message.closed {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 300px;
}

.ecs-notification-item h4 {
  font-weight: bold;
}

.ecs-notification-item .actions {
  padding: 4px 0;
  min-height: 8px;
  text-align: right;
}

.ecs-notification-item .notification-type {
  font-size: 12px;
}

.ecs-notification-item .notification-type-icon {
  max-width: 50%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
  font-size: small;
}

.ecs-notification-item .notification-time {
  font-size: x-small;
  float: right;
}

.ecs-notification-item .icon {
  transform: rotate(0deg);
  transition: transform 250ms linear;
}

.ecs-notification-item .icon.open {
  transform: rotate(-180deg);
  transition: transform 250ms linear;
}

.rounded-corners {
  border-radius: 8px;
}

audio {
  width: calc(100% - 16px);
  margin: 0 8px;
}

audio::-webkit-media-controls-enclosure {
  background-color: var(--v-surface-base);
  border-radius: 4px;
}

audio[dark]::-webkit-media-controls-enclosure {
  background-color: #fff3;
  border-radius: 4px;
  filter: invert(1);
}

.cb-info h3 {
  color: var(--v-primary-base);
  font-size: 12px;
  text-transform: uppercase;
}

.cb-info p {
  font-size: 12px;
  opacity: 0.7;
  white-space: pre-line;
  max-height: 160px;
  overflow: auto;
}

.phone-btn {
  border-radius: 6px;
  margin: 0 0 0 auto;
}

.phone {
  margin-right: 8px;
}
</style>
